import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, errMsg, SUBJECTS, GRADE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, GradeBadge, fmtDate } from "@/components/common";
import { GradeSelect } from "@/pages/AuthPage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Sparkles, Loader2, BookOpen, FolderPlus, Library } from "lucide-react";
import { toast } from "sonner";

const STEPS = ["Planning the learning objectives...", "Writing the lesson sections...", "Choosing key vocabulary...", "Creating quiz questions...", "Checking it fits the grade level..."];
const CAN_AUTHOR = ["teacher", "admin", "parent"];

export const CourseCreateDialog = ({ open, onOpenChange, onCreated }) => {
  const [f, setF] = useState({ title: "", description: "", subject: "Math", grade_band: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.title.trim() || !f.grade_band) return toast.error("Add a title and grade level");
    setBusy(true);
    try {
      const { data } = await api.post("/courses", f);
      toast.success("Course created");
      setF({ title: "", description: "", subject: "Math", grade_band: "" });
      onCreated?.(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white" data-testid="course-create-dialog">
        <DialogHeader><DialogTitle className="font-display">New course</DialogTitle><DialogDescription>Courses appear in the library for everyone. Add lessons after creating it.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label>Title</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="course-title-input" /></div>
          <div className="space-y-2"><Label>Description</Label><Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="course-description-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Subject</Label>
              <Select value={f.subject} onValueChange={(v) => setF({ ...f, subject: v })}>
                <SelectTrigger className="h-11" data-testid="course-subject-select"><SelectValue /></SelectTrigger>
                <SelectContent>{SUBJECTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Grade level</Label><GradeSelect value={f.grade_band} onChange={(v) => setF({ ...f, grade_band: v })} testid="course-grade-select" /></div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={save} disabled={busy} data-testid="course-create-submit">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Create course</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function CreatePage() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const canAuthor = CAN_AUTHOR.includes(user.role);
  const [tab, setTab] = useState("ai");
  const [form, setForm] = useState({ subject: "Math", topic: "", grade_band: user.grade_band || "", length: "standard", notes: "", course_id: params.get("course") || "none" });
  const [myCourses, setMyCourses] = useState([]);
  const [myLessons, setMyLessons] = useState([]);
  const [job, setJob] = useState(null);
  const [step, setStep] = useState(0);
  const [courseOpen, setCourseOpen] = useState(false);
  const poll = useRef(null);

  const loadMine = () => {
    api.get("/lessons/mine").then(({ data }) => setMyLessons(data));
    if (canAuthor) api.get("/courses", { params: { mine: true } }).then(({ data }) => setMyCourses(data));
  };
  useEffect(() => {
    loadMine();
    return () => clearInterval(poll.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const c = myCourses.find((x) => x.course_id === form.course_id);
    if (c) setForm((f) => ({ ...f, grade_band: c.grade_band, subject: SUBJECTS.includes(c.subject) ? c.subject : f.subject }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.course_id, myCourses]);

  useEffect(() => {
    if (!job) return;
    const t = setInterval(() => setStep((s) => (s + 1) % STEPS.length), 4000);
    return () => clearInterval(t);
  }, [job]);

  const generate = async (e) => {
    e.preventDefault();
    if (!form.topic.trim() || !form.grade_band) return toast.error("Add a topic and grade level");
    try {
      const { data } = await api.post("/ai/lessons", { ...form, course_id: form.course_id === "none" ? null : form.course_id });
      setJob(data);
      setStep(0);
      clearInterval(poll.current);
      poll.current = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/ai/jobs/${data.job_id}`);
          if (j.status === "done") {
            clearInterval(poll.current);
            setJob(null);
            toast.success("Your lesson is ready!");
            navigate(`/lessons/${j.lesson_id}`);
          } else if (j.status === "failed") {
            clearInterval(poll.current);
            setJob(null);
            toast.error(j.error || "Generation failed");
          }
        } catch {
          /* keep polling */
        }
      }, 3000);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Create" title="Create lessons" subtitle="Describe any topic and Sage writes a full lesson with vocabulary and a graded quiz, matched to the grade level." />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6 bg-secondary">
          <TabsTrigger value="ai" data-testid="create-tab-ai">AI lesson</TabsTrigger>
          {canAuthor && <TabsTrigger value="courses" data-testid="create-tab-courses">My courses</TabsTrigger>}
          <TabsTrigger value="mine" data-testid="create-tab-mine">My lessons</TabsTrigger>
        </TabsList>

        <TabsContent value="ai">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <form onSubmit={generate} className="lg:col-span-7 bg-white border border-border rounded-[18px] p-6 sm:p-8 space-y-5" data-testid="ai-create-form">
              <div className="space-y-2">
                <Label>Topic</Label>
                <Input value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} placeholder="e.g. The water cycle, Long division, The French Revolution" className="h-11" data-testid="ai-create-topic-input" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Subject</Label>
                  <Select value={form.subject} onValueChange={(v) => setForm({ ...form, subject: v })}>
                    <SelectTrigger className="h-11 bg-white" data-testid="ai-create-subject-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{SUBJECTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Grade level</Label><GradeSelect value={form.grade_band} onChange={(v) => setForm({ ...form, grade_band: v })} testid="ai-create-grade-select" /></div>
              </div>
              <div className="space-y-2"><Label>Length</Label>
                <div className="grid grid-cols-3 gap-2">
                  {[["short", "Short"], ["standard", "Standard"], ["long", "In-depth"]].map(([v, l]) => (
                    <button type="button" key={v} onClick={() => setForm({ ...form, length: v })} className={`h-11 rounded-lg border text-sm font-medium transition-colors duration-150 ${form.length === v ? "border-[#0F7B7D] bg-[#D7F3F1]" : "border-border bg-white hover:bg-secondary"}`} data-testid={`ai-create-length-${v}`}>{l}</button>
                  ))}
                </div>
              </div>
              {canAuthor && (
                <div className="space-y-2"><Label>Add to course (optional)</Label>
                  <Select value={form.course_id} onValueChange={(v) => setForm({ ...form, course_id: v })}>
                    <SelectTrigger className="h-11 bg-white" data-testid="ai-create-course-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Keep as a private lesson</SelectItem>
                      {myCourses.map((c) => <SelectItem key={c.course_id} value={c.course_id}>{c.title} ({c.grade_band})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2"><Label>Anything else? (optional)</Label><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g. Use sports examples, focus on word problems, my child loves dinosaurs" data-testid="ai-create-notes-input" /></div>
              <Button type="submit" className="h-12 w-full sm:w-auto px-6" disabled={!!job} data-testid="ai-create-generate-button">
                {job ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />} {job ? "Writing your lesson..." : "Generate lesson"}
              </Button>
            </form>
            <div className="lg:col-span-5">
              {job ? (
                <div className="bg-[#0F7B7D] text-white rounded-[18px] p-8 fade-up" data-testid="ai-create-progress">
                  <Sparkles className="h-8 w-8 spin-slow" />
                  <div className="mt-4 font-display text-xl font-semibold">Sage is writing "{job.topic}"</div>
                  <div className="mt-2 text-white/85 text-sm">{STEPS[step]}</div>
                  <div className="mt-6 text-xs text-white/70">This usually takes 20–60 seconds. You'll be taken to the lesson when it's ready.</div>
                </div>
              ) : (
                <div className="bg-[#F5F1E6] border border-border rounded-[18px] p-8">
                  <div className="font-display font-semibold text-lg">Every AI lesson includes</div>
                  <ul className="mt-4 space-y-3 text-sm text-[#123645]">
                    <li>• 3–4 learning objectives</li>
                    <li>• Clear sections written for the chosen grade</li>
                    <li>• Key vocabulary with definitions</li>
                    <li>• A 5-question quiz with explanations</li>
                    <li>• Sage, ready to tutor on the lesson</li>
                  </ul>
                  <p className="mt-6 text-xs text-muted-foreground">AI can make mistakes — teachers and parents should review lessons before assigning them.</p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {canAuthor && (
          <TabsContent value="courses">
            <div className="flex justify-end mb-4"><Button onClick={() => setCourseOpen(true)} data-testid="create-course-button"><FolderPlus className="h-4 w-4 mr-2" />New course</Button></div>
            {myCourses.length === 0 ? (
              <EmptyState icon={Library} title="No courses yet" text="Create a course, then write lessons yourself or add them with AI." />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="my-courses-grid">
                {myCourses.map((c) => (
                  <Link key={c.course_id} to={`/courses/${c.course_id}`} className="bg-white border border-border rounded-[14px] p-5 lift" data-testid="my-course-card">
                    <div className="flex items-center gap-2"><GradeBadge grade={c.grade_band} /><span className="text-xs text-muted-foreground">{c.subject}</span></div>
                    <div className="mt-3 font-display font-semibold text-lg">{c.title}</div>
                    <div className="text-xs text-muted-foreground mt-2">{c.lesson_count} lessons</div>
                  </Link>
                ))}
              </div>
            )}
            <CourseCreateDialog open={courseOpen} onOpenChange={setCourseOpen} onCreated={(c) => { setCourseOpen(false); navigate(`/courses/${c.course_id}`); }} />
          </TabsContent>
        )}

        <TabsContent value="mine">
          {myLessons.length === 0 ? (
            <EmptyState icon={BookOpen} title="No lessons yet" text="Lessons you generate or write will appear here." />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="my-lessons-grid">
              {myLessons.map((l) => (
                <Link key={l.lesson_id} to={`/lessons/${l.lesson_id}`} className="bg-white border border-border rounded-[14px] p-5 lift" data-testid="my-lesson-card">
                  <div className="flex items-center gap-2 flex-wrap"><GradeBadge grade={l.grade_band} /><span className="text-xs text-muted-foreground">{l.subject}</span>{l.source === "ai" && <span className="text-xs bg-[#D7F3F1] text-[#0B5B5B] rounded-full px-2 py-0.5">AI</span>}{l.visibility === "private" && <span className="text-xs bg-secondary rounded-full px-2 py-0.5">Private</span>}</div>
                  <div className="mt-3 font-display font-semibold">{l.title}</div>
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{l.summary}</p>
                  <div className="text-xs text-muted-foreground mt-3">{GRADE_LABELS[l.grade_band]} · {fmtDate(l.created_at)}</div>
                </Link>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
